import { FaCheck } from "react-icons/fa";
import { useCallback } from "react";

export const ReturnForm = ({
  billData,
  customerName,
  supplierName,
  returnQuantities,
  note,
  adjustmentType,
  returnDate,
  setCustomerName,
  setSupplierName,
  setReturnQuantities,
  setNote,
  setAdjustmentType,
  setReturnDate,
  handleSubmit,
  handleCancel,
  isCustomerReturn,
  loading,
  error,
  successMessage,
  isItemExpired,
  hasValidReturnQuantities,
  isEditMode,
  setError
}) => {
  // Helper to normalize date to local day (midnight of that day)
  const normalizeDateToLocalDay = useCallback((dateInput) => {
    if (!dateInput) return null;

    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === "string") {
      if (dateInput.includes("T") || dateInput.includes("Z")) {
        date = new Date(dateInput);
      } else {
        const [year, month, day] = dateInput.split("-").map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      return null;
    }

    if (isNaN(date.getTime())) return null;

    const localYear = date.getFullYear();
    const localMonth = date.getMonth();
    const localDay = date.getDate();

    // Create a new Date object at local midnight
    return new Date(localYear, localMonth, localDay);
  }, []);

  const calculateTotalTax = useCallback(() => {
    if (!billData?.items) return 0;
    return billData.items
      .filter((item) => (returnQuantities[item.id] || 0) > 0 && !isItemExpired(item))
      .reduce(
        (sum, item) =>
          sum + (returnQuantities[item.id] || 0) * item.salePrice * ((item.tax || 0) / 100),
        0
      )
      .toFixed(2);
  }, [billData?.items, returnQuantities, isItemExpired]);

  const calculateNetReturn = useCallback(() => {
    if (!billData?.items) return 0;
    return billData.items
      .filter((item) => (returnQuantities[item.id] || 0) > 0 && !isItemExpired(item))
      .reduce((sum, item) => {
        const qty = returnQuantities[item.id] || 0;
        return sum + (qty * item.salePrice * (1 + ((item.tax || 0) / 100)));
      }, 0)
      .toFixed(2);
  }, [billData?.items, returnQuantities, isItemExpired]);

  // Validate return date against bill date
  const validateReturnDate = () => {
    if (!returnDate || !billData?.createdAt) return true;

    const selectedDate = normalizeDateToLocalDay(returnDate);
    const billDate = normalizeDateToLocalDay(billData.createdAt);

    if (!selectedDate || !billDate) {
        console.error("Date normalization failed for validation in ReturnForm.");
        return false;
    }
    
    return selectedDate >= billDate;
  };

  // Determine the minimum allowed date for the returnDate input field
  const minReturnDate = useCallback(() => {
    if (billData?.createdAt) {
        const billCreationDate = normalizeDateToLocalDay(billData.createdAt);
        if (billCreationDate) {
            return billCreationDate.toISOString().split('T')[0];
        }
    }
    const today = new Date();
    return today.toISOString().split('T')[0];
  }, [billData?.createdAt, normalizeDateToLocalDay]);


  return (
    <div className="bg-white shadow-sm rounded-lg mt-6">
      <div className="flex flex-wrap gap-3 flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-5">
        <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
          {isEditMode
            ? isCustomerReturn
              ? "Edit Customer Return"
              : "Edit Supplier Return"
            : isCustomerReturn
            ? "Add Customer Return"
            : "Add Supplier Return"}
        </h2>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!validateReturnDate()) {
            setError("Return date cannot be before the bill/purchase date");
            return;
          }
          handleSubmit(e);
        }}
        className="p-5"
      >
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label">
              {isCustomerReturn ? "Customer Name" : "Supplier Name"}
            </label>
            <input
              type="text"
              className="input-field"
              value={isCustomerReturn ? customerName : supplierName}
              onChange={(e) =>
                isCustomerReturn
                  ? setCustomerName(e.target.value)
                  : setSupplierName(e.target.value)
              }
              placeholder={`Enter ${isCustomerReturn ? "customer" : "supplier"} name`}
              required
              disabled={loading}
            />
          </div>
          <div>
            <label className="label">
              Return Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className="input-field"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              disabled={loading && !isEditMode}
              required
              min={minReturnDate()} // Use the dynamically calculated min date
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="label">
            Notes <span className="text-red-500">*</span>
          </label>
          <textarea
            className="input-field"
            rows="4"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter notes for this return"
            required
            disabled={loading}
          />
        </div>

        {billData?.items?.length > 0 && (
          <table className="w-full mt-6 table-fixed border-collapse min-w-[1000px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Medicine Name
                </th>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Original Qty {isCustomerReturn ? "(Sold)" : "(Purchased)"}
                </th>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Available Stock
                </th>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Return Qty *
                </th>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Unit Price {isCustomerReturn ? "(Sale)" : "(Supplier)"}
                </th>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Tax Amount
                </th>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Total
                </th>
                <th className="text-left text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                  Check Return *
                </th>
              </tr>
            </thead>
            <tbody>
              {billData.items.map((item) => {
                const qty = returnQuantities[item.id] || 0;
                const taxAmount = qty * item.salePrice * ((item.tax || 0) / 100);
                const totalAfterDeduction = qty * item.salePrice * (1 + ((item.tax || 0) / 100));

                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="text"
                        className="input-field disabled:bg-gray-100"
                        value={item.medicineName}
                        readOnly
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        className="input-field disabled:bg-gray-100"
                        value={item.quantity} // Original sold/purchased quantity
                        readOnly
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        className="input-field disabled:bg-gray-100"
                        value={item.stockQty} // Live available stock
                        readOnly
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        className="input-field disabled:bg-gray-100"
                        min="0"
                        max={item.quantity} // Max returnable quantity should be original quantity from bill, not stock.
                                            // Customer can only return what they bought from *this* bill.
                                            // Supplier can only return what they bought in *this* purchase.
                        value={qty}
                        onChange={(e) => {
                          const val = Math.max(
                            0,
                            Math.min(
                              parseInt(e.target.value) || 0,
                              item.quantity // Limit by original quantity from this bill/purchase
                            )
                          );
                          setReturnQuantities((prev) => ({
                            ...prev,
                            [item.id]: val,
                          }));
                        }}
                        disabled={loading || isItemExpired(item)}
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        className="input-field disabled:bg-gray-100"
                        value={item.salePrice.toFixed(2)}
                        readOnly
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        className="input-field disabled:bg-gray-100 text-end"
                        value={taxAmount.toFixed(2)}
                        readOnly
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        className="input-field disabled:bg-gray-100 text-end"
                        value={totalAfterDeduction.toFixed(2)}
                        readOnly
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={`agreement-${item.id}`}
                          className="hidden peer"
                          disabled={loading || isItemExpired(item) || qty <= 0}
                          checked={qty > 0 && !isItemExpired(item)}
                          readOnly
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-white peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                          <FaCheck className="text-white text-xs" />
                        </span>
                        <label
                          htmlFor={`agreement-${item.id}`}
                          className="cursor-pointer font-jost font-normal text-base text-slate-600 relative z-10 pl-6"
                        ></label>
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td
                  className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                  colSpan={7}
                >
                  Total Tax
                </td>
                <td className="border border-gray-200 py-2 px-3">
                  <input
                    type="number"
                    value={calculateTotalTax()}
                    readOnly
                    className="input-field text-end disabled:bg-gray-100"
                    placeholder="0.00"
                  />
                </td>
              </tr>
              <tr>
                <td
                  className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                  colSpan={7}
                >
                  Net Return
                </td>
                <td className="border border-gray-200 py-2 px-3">
                  <input
                    type="number"
                    value={calculateNetReturn()}
                    readOnly
                    className="input-field text-end disabled:bg-gray-100"
                    placeholder="0.00"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <div className="mt-4">
          <label className="mr-4 label">
            <input
              type="radio"
              value="stock"
              checked={adjustmentType === "stock"}
              onChange={() => setAdjustmentType("stock")}
              disabled={loading || billData?.items.some(isItemExpired)}
            />{" "}
            Adjust With Stock
          </label>
          <label className="label">
            <input
              type="radio"
              value="wastage"
              checked={adjustmentType === "wastage"}
              onChange={() => setAdjustmentType("wastage")}
              disabled={loading || billData?.items.some(isItemExpired)}
            />{" "}
            Wastage
          </label>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={
              loading ||
              !hasValidReturnQuantities ||
              (isCustomerReturn && !customerName) ||
              (!isCustomerReturn && !supplierName) ||
              !note.trim() ||
              !validateReturnDate() // Use validateReturnDate for form submission
            }
          >
            {loading
              ? "Processing..."
              : isEditMode
              ? "Update Return"
              : "Submit Return"}
          </button>
        </div>
        {successMessage && <p className="text-green-500 mt-2">{successMessage}</p>}
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </form>
    </div>
  );
};